import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { api, fmtDate } from "@/lib/api";
import { PageTitle, Panel } from "@/pages/admin/AdminLayout";

const STATUS = { published: "Publicado", reserved: "Reservado", sold: "Vendido", draft: "Rascunho" };
const shortDay = (d) => d.slice(8, 10) + "/" + d.slice(5, 7);

function Kpis({ totals, days }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
      <Panel><p className="text-[11px] uppercase tracking-[0.2em] text-[#A39E93]">Partilhas · últimos {days} dias</p><p className="mt-2 text-3xl font-display text-[#F3EFE6]" data-testid="shares-total-period">{totals.period}</p></Panel>
      <Panel><p className="text-[11px] uppercase tracking-[0.2em] text-[#A39E93]">Partilhas · desde sempre</p><p className="mt-2 text-3xl font-display text-[#F3EFE6]" data-testid="shares-total-all">{totals.all_time}</p></Panel>
      <Panel><p className="text-[11px] uppercase tracking-[0.2em] text-[#A39E93]">Média / dia</p><p className="mt-2 text-3xl font-display text-[#F3EFE6]" data-testid="shares-avg">{(totals.period / Math.max(1, days)).toFixed(1)}</p></Panel>
    </div>
  );
}

function Chart({ series }) {
  return (
    <Panel className="mb-6" data-testid="shares-chart">
      <p className="text-sm mb-4">Partilhas por dia</p>
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={series} margin={{ left: -20, right: 8, top: 4 }}>
            <defs><linearGradient id="gs" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#C8AE6E" stopOpacity={0.35} /><stop offset="100%" stopColor="#C8AE6E" stopOpacity={0} /></linearGradient></defs>
            <CartesianGrid stroke="#282520" vertical={false} />
            <XAxis dataKey="day" tickFormatter={shortDay} tick={{ fill: "#A39E93", fontSize: 11 }} stroke="#282520" minTickGap={18} />
            <YAxis allowDecimals={false} tick={{ fill: "#A39E93", fontSize: 11 }} stroke="#282520" />
            <Tooltip contentStyle={{ background: "#141310", border: "1px solid #282520", fontSize: 12 }} labelFormatter={shortDay} formatter={(v) => [v, "Partilhas"]} />
            <Area type="monotone" dataKey="shares" stroke="#C8AE6E" strokeWidth={1.5} fill="url(#gs)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Panel>
  );
}

function Ranking({ rows }) {
  const max = Math.max(1, ...rows.map((x) => x.period_count));
  if (!rows.length) return <Panel><p className="text-sm text-[#A39E93]" data-testid="shares-empty">Ainda sem partilhas registadas neste período.</p></Panel>;
  return (
    <Panel className="overflow-x-auto" data-testid="shares-ranking">
      <table className="w-full text-sm min-w-[760px]">
        <thead><tr className="text-left text-[11px] uppercase tracking-[0.15em] text-[#A39E93]"><th className="pb-3 w-8">#</th><th className="pb-3">Relógio</th><th className="pb-3 text-right pr-4">No período</th><th className="pb-3 text-right pr-6">Total</th><th className="pb-3 pl-2">Última partilha</th><th className="pb-3 pl-4 w-40">Procura</th></tr></thead>
        <tbody>
          {rows.map((x, i) => (
            <tr key={x.watch_id} className="border-t border-[#282520]" data-testid={`shares-row-${x.slug}`}>
              <td className="py-3 text-[#A39E93]">{i + 1}</td>
              <td className="py-3"><Link to={`/admin/relogios/${x.watch_id}`} className="text-[#E6D5AC] hover:underline"><span className="block text-[10px] uppercase tracking-[0.2em] text-[#C8AE6E]">{x.brand}</span>{x.model}</Link><p className="text-[11px] text-[#A39E93]">{STATUS[x.status] || x.status}</p></td>
              <td className="py-3 text-right tabular-nums pr-4" data-testid={`shares-period-${x.slug}`}>{x.period_count}</td>
              <td className="py-3 text-right tabular-nums text-[#A39E93] pr-6" data-testid={`shares-total-${x.slug}`}>{x.total_count}</td>
              <td className="py-3 pl-2 text-[11px] text-[#A39E93]">{x.last_shared_at ? fmtDate(x.last_shared_at) : "—"}</td>
              <td className="py-3 pl-4"><div className="flex items-center gap-3"><div className="h-1.5 flex-1 bg-[#282520]"><div className="h-full bg-[#C8AE6E]" style={{ width: `${(x.period_count / max) * 100}%` }} /></div><span className="tabular-nums text-xs text-[#E6D5AC] w-8 text-right">{x.period_count}</span></div></td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-[11px] text-[#A39E93] mt-4">Cada vez que um admin usa o botão «Partilhar» na lista de relógios, é incrementada uma partilha e copiada a ligação pública (apontando para o URL de produção configurado em Definições).</p>
    </Panel>
  );
}

export default function Shares() {
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { let live = true; setD(null); setErr(false); api.get(`/admin/shares/report?days=${days}`).then((x) => live && setD(x.data)).catch(() => live && setErr(true)); return () => { live = false; }; }, [days]);
  return (
    <div data-testid="admin-shares">
      <PageTitle title="Relatório de Partilhas" sub="Quantas vezes cada relógio foi partilhado pela equipa. As partilhas são feitas a partir da lista de relógios no backoffice." />
      <div className="flex gap-2 mb-6">{[7, 30, 90].map((n) => <button key={n} onClick={() => setDays(n)} className={days === n ? "btn-gold !py-1.5 !px-4 text-xs" : "btn-line !py-1.5 !px-4 text-xs"} data-testid={`shares-days-${n}`}>{n} dias</button>)}</div>
      {!d && !err && <p className="text-sm text-[#A39E93] animate-pulse" data-testid="shares-loading">A carregar…</p>}
      {err && <p className="text-sm text-[#e08f7f]" data-testid="shares-error">Não foi possível carregar o relatório. Tente novamente.</p>}
      {d && <><Kpis totals={d.totals} days={d.days} /><Chart series={d.series} /><Ranking rows={d.ranking} /></>}
    </div>
  );
}
